import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { DialogImportProjectComponent } from 'src/app/components/shared/dialog-import-project/dialog-import-project.component';
import { DialogShareProjectComponent } from 'src/app/components/shared/dialog-share-project/dialog-share-project.component';
import { environment } from 'src/environments/environment';
import { FunctionalityCode } from '../_enums/functionality.code';
import { GroupBy } from '../_enums/groupby';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { SnackbarTypes } from '../_enums/snackbar.types';
import { AdherentModel } from '../_models/adherent';
import { CeuValue } from '../_models/ceuValues';
import { DetailedOperation } from '../_models/detailedOperation';
import { Indicator } from '../_models/indicator';
import { Operation } from '../_models/operation';
import { PdfPictures } from '../_models/pdfPictures';
import { Project } from '../_models/project';
import { ProjectAgencyAssociation } from '../_models/projectAgencyAssociation';
import { PublishProject } from '../_models/publishProject';
import { Scenario } from '../_models/scenario';
import { Solution } from '../_models/solution';
import { AuthService } from './auth.service';
import { SnackbarService } from './snackbar.service';

interface ImportProjectResponse {
  importedProjectId: string;
  isSuccessful: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  groupBy?: GroupBy;
  projectDeleted$ = new Subject<string[]>();
  projectCreated$ = new Subject<Project>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private matDialog: MatDialog,
    private router: Router,
    private translate: TranslateService
  ) {}

  get(projectId: string) {
    return this.http.get<Project>(`${environment.apiUrl}/projects/${projectId}`, { headers: this.authService.headers });
  }

  getAll(groupBy?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;

    return this.http.get<Project[]>(`${environment.apiUrl}/projects`, {
      params,
      headers: this.authService.headers
    });
  }

  CheckResourceForProjectVersion(data: DetailedOperation) {
    return this.http.post<Operation>(`${environment.apiUrl}/projects/ResourceAvailableForprojectVersion`, data, { headers: this.authService.headers });
  }

  update(project: Project) {
    return this.http.put<Project>(`${environment.apiUrl}/projects/${project.id}`, project, { headers: this.authService.headers });
  }

  add(project: Project) {
    return this.http.post<Project>(`${environment.apiUrl}/projects`, project, {
      headers: this.authService.headers
    });
  }

  getFromCft(cftId: string): Observable<Project> {
    return this.http.get<Project>(`${environment.apiUrl}/Projects/GetFromCft`, {
      params: { cftId },
      headers: this.authService.headers
    });
  }

  delete(resourceId: string) {
    return this.http.delete(`${environment.apiUrl}/projects/${resourceId}`, {
      headers: this.authService.headers
    });
  }

  deleteMany(resourceIds: string[]): Observable<{ results: { projectId: string; success: boolean }[] }> {
    return this.http.delete<{ results: { projectId: string; success: boolean }[] }>(`${environment.apiUrl}/projects/deleteBulk`, {
      headers: this.authService.headers,
      body: { projectIds: resourceIds }
    });
  }

  duplicate(resourceId: string) {
    return this.http.post<Project>(`${environment.apiUrl}/projects/duplicate?id=${resourceId}`, null, { headers: this.authService.headers });
  }

  getIndicators() {
    return this.http.get<Indicator[]>(`${environment.apiUrl}/projects/Indicators`, { headers: this.authService.headers });
  }

  getScenarioById(scenarioId: string) {
    return this.http.get<Scenario>(`${environment.apiUrl}/projects/Scenario/${scenarioId}`, { headers: this.authService.headers });
  }

  getScenarioByProjectId(projectId: string) {
    return this.http.get<Scenario[]>(`${environment.apiUrl}/projects/Scenario/byProjectId/${projectId}`, { headers: this.authService.headers });
  }

  addScenario(scenario: Scenario) {
    return this.http
      .post<Scenario>(`${environment.apiUrl}/projects/Scenario/`, scenario, {
        headers: this.authService.headers
      })
      .toPromise();
  }

  deleteScenario(scenarioId: string) {
    return this.http
      .delete(`${environment.apiUrl}/projects/Scenario/${scenarioId}`, {
        headers: this.authService.headers
      })
      .toPromise();
  }

  updateScenario(scenario: Scenario) {
    return this.http
      .put<Scenario>(`${environment.apiUrl}/projects/Scenario/`, scenario, {
        headers: this.authService.headers
      })
      .toPromise();
  }

  deleteSolutionScenario(scenarioId?: string, solutionId?: string, rank?: number) {
    return this.http.delete(`${environment.apiUrl}/projects/scenario/${scenarioId}/solution/${solutionId}?rank=${rank}`, { headers: this.authService.headers }).toPromise();
  }

  addSolutionScenario(scenarioId?: string, solution?: Solution, rank?: number) {
    return this.http.post(`${environment.apiUrl}/projects/scenario/${scenarioId}/solution?rank=${rank}`, solution, { headers: this.authService.headers }).toPromise();
  }

  editRank(scenarioId?: string, solutions?: Solution[]) {
    return this.http.put(`${environment.apiUrl}/projects/scenario/${scenarioId}/solutions/`, solutions, { headers: this.authService.headers }).toPromise();
  }

  getSynthese(scenarioId?: string) {
    return this.http.get<any>(`${environment.apiUrl}/projects/scenario/${scenarioId}/synthesis`, { headers: this.authService.headers }).toPromise();
  }

  getGenerateExcel(scenarioId?: string) {
    return this.http.get<Blob>(`${environment.apiUrl}/projects/scenario/${scenarioId}/synthesis/xlsx`, { headers: this.authService.headers, responseType: 'blob' as 'json' });
  }

  getGeneratePDF(scenarioId?: string, pictures?: PdfPictures) {
    return this.http.post<Solution>(`${environment.apiUrl}/projects/scenario/${scenarioId}/synthesisPDF`, pictures, { headers: this.authService.headers });
  }

  getEUsToPublishProject(projectId: string) {
    return this.http.get<AdherentModel[]>(`${environment.apiUrl}/projects/PublishProject/${projectId}`, { headers: this.authService.headers });
  }

  getAgencyAssociations(projectId: string) {
    return this.http.get<ProjectAgencyAssociation[]>(`${environment.apiUrl}/projects/getAgencyAssociations/${projectId}`, { headers: this.authService.headers });
  }

  setAgencyAssociations(projectId: string, agenciesIds: string[]) {
    return this.http.put(`${environment.apiUrl}/projects/setAgencyAssociations/${projectId}`, agenciesIds, { headers: this.authService.headers });
  }

  publishProject(publishProject: PublishProject) {
    return this.http.put<Project>(`${environment.apiUrl}/projects/publish`, publishProject, { headers: this.authService.headers });
  }

  getProjectsListForScenario(projectId: string) {
    return this.http.get<Project[]>(`${environment.apiUrl}/projects/Scenario/getProjectsList/${projectId}`, { headers: this.authService.headers });
  }

  getSynthesisVerificationDataExcel(scenario: Scenario) {
    return this.http.post<Blob>(`${environment.apiUrl}/projects/scenario/synthesis/verificationData/xlsx`, scenario, { headers: this.authService.headers, responseType: 'blob' as 'json' });
  }

  getProjectsToCopyOperation(projectId: string) {
    return this.http.get<Project[]>(`${environment.apiUrl}/projects/getProjectsToCopyOperation?projectId=${projectId}`, { headers: this.authService.headers });
  }

  changeProjectAgencyOwner(projectId: string, agencyId: string) {
    return this.http.put<Project>(`${environment.apiUrl}/projects/changeProjectAgencyOwner/${projectId}?agencyId=${agencyId}`, null, { headers: this.authService.headers });
  }

  getProjectCost(data: DetailedOperation) {
    return this.http.post<CeuValue[]>(`${environment.apiUrl}/projects/projectCosts`, data, { headers: this.authService.headers });
  }

  getSyntheseValuesForGraph(scenarioId?: string) {
    return this.http.get<any>(`${environment.apiUrl}/projects/scenario/${scenarioId}/synthesis/graph`, { headers: this.authService.headers }).toPromise();
  }
  getSunburstValuesToGraph(scenarioId: any): any {
    return this.http.get<any>(`${environment.apiUrl}/projects/scenario/${scenarioId}/synthesis/graph/sunburst`, { headers: this.authService.headers }).toPromise();
  }

  shareProjectsToAgencies(projectIds: string[], agenciesIds: string[]) {
    return this.http.put(`${environment.apiUrl}/projects/setBulkAgencyAssociations`, { projectIds, agenciesIds }, { headers: this.authService.headers });
  }

  importProject(formData: FormData) {
    return this.http.post<ImportProjectResponse>(`${environment.apiUrl}/Projects/Import`, formData, { headers: this.authService.headers });
  }

  exportProject(projectId: string) {
    return this.http.get(`${environment.apiUrl}/Projects/Export/${projectId}`, {
      headers: this.authService.headers,
      responseType: 'blob'
    });
  }

  onShareProjects(selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: NavListCommonProps['updateDataSource']) {
    if (selectedItems.length === 0) {
      this.snackbarService.open(SnackbarTypes.error, this.translate.instant('error_no_project_selected'));
      return;
    }

    const selectedProjectIds = selectedItems.map((project) => project.id);

    const shareDialogRef = this.matDialog.open(DialogShareProjectComponent, {
      data: { projectIds: selectedProjectIds }
    });

    shareDialogRef.afterClosed().subscribe((dataResult) => {
      if (dataResult) {
        updateDataSource(dataSource.data.map((project) => ({ ...project, selected: false })));
      }
    });
  }

  onDeleteProjects(selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: (newDataSource: any[]) => void, navComponent: any) {
    return navComponent.createDeleteHandler({
      deleteMany: (ids: string[]) => this.deleteMany(ids),
      getItemId: (item: any) => item?.id,
      filterData: (data: any[], deletedIds: string[]) => data.filter((item) => !deletedIds.includes(item.id)),
      successMessage: (count: number) => this.translate.instant(count > 1 ? 'success_projects_deleted' : 'success_project_deleted'),
      errorMessage: this.translate.instant('error_no_project_selected'),
      snackbarService: this.snackbarService
    })(selectedItems, dataSource, updateDataSource);
  }

  onClickNewItem(perimeterId: string) {
    this.router.navigate([`${SiteMapUrl.MenuProject}/new`], { state: { perimeterId: perimeterId, groupBy: this.groupBy } });
  }

  onImportProject(perimeterId: string) {
    const importDialogRef = this.matDialog.open(DialogImportProjectComponent, {
      data: { perimeterId }
    });

    importDialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.getAll(this.groupBy);
      }
    });
  }

  private getFilters() {
    const baseFilters = [
      { label: 'ressource_list_filter_all', value: 'all' },
      { label: 'ressource_list_filter_pending', value: 'pending' },
      { label: 'ressource_list_filter_model', value: 'model' },
      { label: 'ressource_list_filter_handed', value: 'handed' },
      { label: 'ressource_list_filter_finished', value: 'finished' },
      { label: 'ressource_list_filter_archived', value: 'archived' },
      { label: 'ressource_list_filter_published', value: 'publish' }
    ];

    return baseFilters;
  }

  getNavConfig({ perimeterId, dataSource, updateDataSource, navComponent }: NavListCommonProps) {
    const canImportProject = localStorage.getItem('userRight')?.includes(FunctionalityCode.ImportProject) || false;
    return {
      header: {
        title: 'ressource_list_title_projects',
        actions: [
          { label: 'ressource_list_action_create_project', onClick: () => this.onClickNewItem(perimeterId) },
          { label: 'ressource_list_action_import_project', onClick: () => this.onImportProject(perimeterId), condition: canImportProject }
        ],
        filters: this.getFilters(),
        defaultFilter: GroupBy.pending,
        filterActionList: () => {
          return [
            { label: this.translate.instant('ressource_list_action_create_project'), onClick: () => this.onClickNewItem(perimeterId) },
            { label: this.translate.instant('ressource_list_action_import_project'), onClick: () => this.onImportProject(perimeterId), condition: canImportProject }
          ];
        }
      },
      getDataSource: (groupBy?: GroupBy) => this.getAll(groupBy),
      getItemConfig: (node: any) => {
        return {
          displayName: node.code ? `${node.displayName} [${node.code}]` : node.displayName,
          url: `${SiteMapUrl.MenuProject}/${node.id}`,
          itemType: 'project-list',
          state: {
            projectId: node.id,
            perimeterId: perimeterId,
            groupBy: this.groupBy
          }
        };
      },
      onShare: (selectedItems: any[]) => this.onShareProjects(selectedItems, dataSource, updateDataSource),
      onDelete: (selectedItems: any[]) => this.onDeleteProjects(selectedItems, dataSource, updateDataSource, navComponent)
    };
  }
}
